import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, deliveryEstimate } from "../lib/api";
import { useCart } from "../context/CartContext";
import { track } from "../lib/analytics";
import { fbTrack } from "../lib/fbpixel";
import { Check, ArrowLeft, ChevronDown, FileCheck, Truck, FlaskConical, ShieldCheck, Layers } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState(1);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [openAcc, setOpenAcc] = useState("benefits");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(0);
        setTier(1);
        setVariant(r.data.variants && r.data.variants.length ? r.data.variants[0] : null);
        track("product_view", { product_slug: r.data.slug, product_name: r.data.name });
        fbTrack("ViewContent", {
          content_ids: [r.data.slug], content_name: r.data.name, content_type: "product",
          value: r.data.price, currency: "USD",
        });
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="max-w-[1400px] mx-auto px-5 py-40 text-center text-sm tracking-widest uppercase text-white/60">Loading...</div>;
  }
  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 py-40 text-center">
        <p className="text-sm tracking-[0.2em] uppercase mb-6 text-white">Product not found</p>
        <Link to="/" className="text-xs tracking-[0.2em] uppercase font-bold border-b border-electric text-electric pb-1">Back to Shop</Link>
      </div>
    );
  }

  const isBundle = !!product.is_bundle;
  const gallery = variant?.image ? [variant.image] : (product.images?.length ? product.images : [product.image]);
  const bundleSum = product.compare_at_price || product.price;
  const bundleSave = Math.max(0, +(bundleSum - product.price).toFixed(2));

  const acc = [
    { key: "benefits", title: "Highlights", content: (
      <ul className="space-y-3">
        {(product.benefits || []).map((b) => (
          <li key={b} className="flex items-start gap-3 text-sm text-white/80"><Check size={16} className="mt-0.5 shrink-0 text-electric" /><span>{b}</span></li>
        ))}
        {isBundle && (product.components || []).map((c) => (
          <li key={c.slug} className="flex items-start gap-3 text-sm text-white/80"><Check size={16} className="mt-0.5 shrink-0 text-electric" /><span>{c.name}</span></li>
        ))}
      </ul>
    )},
    { key: "ingredients", title: "Composition", content: <p className="text-sm leading-relaxed text-white/60">{product.ingredients || "Documented per batch. A certificate matched to your lot is included with every order."}</p> },
    { key: "usage", title: "Handling", content: <p className="text-sm leading-relaxed text-white/60">Supplied as {product.servings || "a research vial"}. Store cool and dry. For laboratory research use only, not for human or animal consumption.</p> },
  ];

  const specs = (() => {
    const isBac = product.slug === "bac-water-10ml";
    return [
      { label: "Form", value: isBac ? "Sterile solution" : "Lyophilized" },
      { label: "Purity", value: isBac ? "0.9% BAC" : "99% or higher, HPLC" },
      { label: "Storage", value: isBac ? "Room temp" : "Cool and dry" },
    ];
  })();
  const badges = [
    { icon: FlaskConical, t: "Third party tested" },
    { icon: FileCheck, t: "COA with every order" },
    { icon: Truck, t: "Discreet tracked shipping" },
    { icon: ShieldCheck, t: "Free shipping over $200" },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8 lg:py-12 bg-[#070B1C]">
      <Link to={isBundle ? "/bundles" : "/"} className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold mb-8 text-white/70 hover:text-electric transition-colors duration-200" data-testid="back-to-shop">
        <ArrowLeft size={14} /> {isBundle ? "Back to Protocols" : "Back to Shop"}
      </Link>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <div className="border border-white/15 aspect-square bg-[#0E1430] overflow-hidden sq-lg" data-testid="product-main-image">
            <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-3">
              {gallery.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 border bg-[#0E1430] overflow-hidden sq-md ${activeImg === i ? "border-electric ring-1 ring-electric" : "border-white/20"}`} data-testid={`thumb-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:py-4">
          {product.badge && (
            <span className="inline-block bg-electric text-black text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1 mb-4 sq-xs">{product.badge}</span>
          )}
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white" data-testid="product-title">{product.name}</h1>
          <p className="text-base text-white/60 mt-5 leading-relaxed">{product.description}</p>

          <div className="mt-6 border border-electric/30 bg-[#0B1024] p-5 sq-lg" data-testid="coa-panel">
            <div className="flex items-center gap-2 mb-2">
              <FileCheck size={16} className="text-electric" />
              <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-electric" data-testid="coa-included">Certificate of analysis included</span>
            </div>
            <p className="text-[13px] text-white/65 leading-relaxed">
              Every order ships with a certificate of analysis for the exact vial you receive. Your
              independent lab results, matched to your batch and lot number, travel with the product.
            </p>
          </div>

          {isBundle ? (
            /* BUNDLE purchase block */
            <>
              <div className="mt-8 border border-white/12 bg-[#0B1024] sq-lg overflow-hidden" data-testid="bundle-contents">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10">
                  <Layers size={16} className="text-electric" />
                  <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/70">What is inside</span>
                </div>
                {(product.components || []).map((c, i) => (
                  <div key={c.slug || i} className={`flex items-center justify-between px-5 py-3.5 ${i ? "border-t border-white/8" : ""}`}>
                    {c.slug ? (
                      <Link to={`/products/${c.slug}`} className="text-sm text-white hover:text-electric transition-colors duration-200">{c.name}</Link>
                    ) : (
                      <span className="text-sm text-white">{c.name}</span>
                    )}
                    <span className="text-sm text-white/50">{money(c.price)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-baseline gap-3 flex-wrap">
                <span className="font-display font-bold text-3xl text-white" data-testid="product-price">{money(product.price)}</span>
                {bundleSave > 0 && <span className="text-base text-white/40 line-through">{money(bundleSum)}</span>}
              </div>
              <div className="mt-4 flex items-center gap-2 text-[12px] text-white/55" data-testid="delivery-estimate">
                <Truck size={14} className="text-electric shrink-0" />
                <span>Order today, get it by <span className="text-white font-semibold">{deliveryEstimate()}</span>. Ships next business day.</span>
              </div>

              <button
                onClick={() => addItem(product, { tierQty: 1, packPrice: product.price })}
                className="btn-primary w-full mt-6 py-4 text-xs tracking-[0.2em] uppercase font-bold"
                data-testid="pdp-add-to-cart"
              >
                Add Protocol to Cart {money(product.price)}
              </button>
              <button
                onClick={() => { addItem(product, { tierQty: 1, packPrice: product.price }); setOpen(true); }}
                className="btn-ghost w-full mt-3 py-4 text-xs tracking-[0.2em] uppercase font-bold"
                data-testid="pdp-buy-now"
              >
                Buy Now
              </button>
            </>
          ) : (
            /* STANDARD product purchase block */
            (() => {
              const tiers = variant?.tiers?.length ? variant.tiers : (product.tiers?.length ? product.tiers : [{ qty: 1, price: variant?.price ?? product.price }]);
              const single = tiers[0].price;
              const selected = tiers.find((t) => t.qty === tier) || tiers[0];
              const perVial = selected.price / selected.qty;
              const savePct = Math.round((1 - perVial / single) * 100);
              return (
                <>
                  <div className="flex items-baseline gap-3 mt-8 flex-wrap">
                    <span className="font-display font-bold text-3xl text-white" data-testid="product-price">{money(selected.price)}</span>
                    {selected.qty > 1 && <span className="text-sm text-white/50">{money(perVial)} / vial</span>}
                    <span className="text-xs tracking-[0.15em] uppercase text-white/40">{selected.qty} vial{selected.qty > 1 ? "s" : ""}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-[12px] text-white/55" data-testid="delivery-estimate">
                    <Truck size={14} className="text-electric shrink-0" />
                    <span>Order today, get it by <span className="text-white font-semibold">{deliveryEstimate()}</span>. Ships next business day.</span>
                  </div>

                  {product.in_stock ? (
                    <>
                      {product.variants && product.variants.length > 1 && (
                        <div className="mt-7">
                          <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/50 mb-3">Choose strength</p>
                          <div className="grid grid-cols-2 gap-3" data-testid="variant-selector">
                            {product.variants.map((v) => {
                              const active = variant && v.label === variant.label;
                              return (
                                <button key={v.label} onClick={() => { setVariant(v); setTier(1); setActiveImg(0); }} className={`border p-4 text-left transition-colors duration-200 sq-md sq-press ${active ? "border-electric bg-electric/10" : "border-white/20 hover:border-electric/60"}`} data-testid={`variant-${v.label}`}>
                                  <div className="font-display font-bold text-lg text-white">{v.label}</div>
                                  <div className="text-sm text-white/70 mt-1">from {money(v.price)}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div className="mt-7">
                        <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/50 mb-3">Choose your pack</p>
                        <div className="grid grid-cols-3 gap-3" data-testid="tier-selector">
                          {tiers.map((t) => {
                            const pv = t.price / t.qty;
                            const sv = Math.round((1 - pv / single) * 100);
                            const active = t.qty === tier;
                            const best = t.qty === tiers[tiers.length - 1].qty && sv > 0;
                            return (
                              <button key={t.qty} onClick={() => setTier(t.qty)} className={`relative border p-4 text-left transition-colors duration-200 sq-md sq-press ${active ? "border-electric bg-electric/10" : "border-white/20 hover:border-electric/60"} ${best ? "ring-2 ring-electric/70" : ""}`} data-testid={`tier-${t.qty}`}>
                                {best && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-electric text-white text-[8px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 whitespace-nowrap">Best Value</span>}
                                <div className="font-display font-bold text-lg text-white">{t.qty} {t.qty === 1 ? "Vial" : "Vials"}</div>
                                <div className="text-sm text-white mt-1">{money(t.price)}</div>
                                <div className="text-[10px] text-white/50 mt-0.5">{money(pv)}/vial</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <button
                        onClick={() => addItem(product, { tierQty: selected.qty, packPrice: selected.price, variantLabel: variant?.label, variantImage: variant?.image })}
                        className="btn-primary w-full mt-6 py-4 text-xs tracking-[0.2em] uppercase font-bold"
                        data-testid="pdp-add-to-cart"
                      >
                        Add to Cart {money(selected.price)}
                      </button>
                      <button
                        onClick={() => { addItem(product, { tierQty: selected.qty, packPrice: selected.price, variantLabel: variant?.label, variantImage: variant?.image }); setOpen(true); }}
                        className="btn-ghost w-full mt-3 py-4 text-xs tracking-[0.2em] uppercase font-bold"
                        data-testid="pdp-buy-now"
                      >
                        Buy Now
                      </button>
                    </>
                  ) : (
                    <button disabled className="w-full mt-8 py-4 text-xs tracking-[0.2em] uppercase font-bold border border-white/15 text-white/40 cursor-not-allowed sq-md" data-testid="pdp-out-of-stock">Out of Stock</button>
                  )}
                </>
              );
            })()
          )}

          {/* Specs + trust (standard products only) */}
          {!isBundle && (
            <>
              <div className="mt-8 grid grid-cols-3 gap-px bg-white/10 border border-white/10 sq-md overflow-hidden" data-testid="product-specs">
                {specs.map((s) => (
                  <div key={s.label} className="bg-[#0B1024] p-4">
                    <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">{s.label}</div>
                    <div className="text-sm font-bold text-white">{s.value}</div>
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="product-trust-badges">
            {badges.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.t} className="flex items-center gap-2 border border-white/10 bg-[#0B1024] px-3 py-2.5 sq-sm">
                  <Icon size={15} className="text-electric shrink-0" />
                  <span className="text-[10px] leading-tight tracking-wide uppercase font-semibold text-white/70">{b.t}</span>
                </div>
              );
            })}
          </div>

          {/* Accordions */}
          <div className="mt-10 border-t border-white/15">
            {acc.map((a) => (
              <div key={a.key} className="border-b border-white/15">
                <button onClick={() => setOpenAcc(openAcc === a.key ? "" : a.key)} className="w-full flex items-center justify-between py-5 text-left text-white" data-testid={`accordion-${a.key}`}>
                  <span className="font-display font-bold text-sm tracking-[0.15em] uppercase">{a.title}</span>
                  <ChevronDown size={18} className={`transition-transform duration-200 text-electric ${openAcc === a.key ? "rotate-180" : ""}`} />
                </button>
                {openAcc === a.key && <div className="pb-6">{a.content}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
